// src/components/report/FunctionRadar.tsx
import * as React from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, Legend, ResponsiveContainer,
} from "recharts";
import type { FnKey } from "@/reports/narratives";

export type FnScore = { fn: FnKey; value: number }; // 0–100

const ORDER: FnKey[] = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"];

export function FunctionRadar({ 
  scores, 
  title = "Function Radar",
  orgName = "Organization",
}: { 
  scores: FnScore[]; 
  title?: string;
  orgName?: string;
}) {
  // Keep only known keys, clamp values, and order them
  const byFn = new Map<FnKey, number>();
  scores.forEach(s => {
    const v = Math.max(0, Math.min(100, Math.round(s.value ?? 0)));
    if ((ORDER as string[]).includes(s.fn)) byFn.set(s.fn as FnKey, v);
  });
  // Always show all 6 functions; default to 0 if missing
  ORDER.forEach(fn => { if (!byFn.has(fn)) byFn.set(fn, 0); });

  const ordered = ORDER
    .map(fn => ({
      name: fn,
      org: byFn.get(fn)!,
    }));

  // If no data, render nothing (safe)
  if (!ordered.length) return null;

  // traffic-light colors
  function trafficLight(score: number) {
    if (score >= 80) return '#1e7e34';
    if (score >= 50) return '#ffbf00';
    return '#cc3030';
  }

  return (
    <section className="print-card-reset avoid-break rounded-2xl border p-4 md:p-6">
      <h2 className="text-lg md:text-xl font-semibold mb-2">{title}</h2>
      <div style={{ width: '420px', height: '300px', margin: '0 auto' }}>
        <RadarChart width={420} height={300} outerRadius="70%" data={ordered}>
          <PolarGrid />
          <PolarAngleAxis dataKey="name" />
          <Radar 
            name={orgName} 
            dataKey="org" 
            stroke="#1f77b4" 
            fill="#1f77b4" 
            fillOpacity={0.35} 
          />
          <Legend />
        </RadarChart>
      </div>

      {/* Industry benchmark note */}
      <div className="mt-2 text-center">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground italic">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-300" aria-hidden />
          Industry Benchmark: Under Development
        </span>
      </div>

      {/* Per-function compare boxes */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${Math.min(ordered.length, 6)}, 1fr)`, 
        gap: 8, 
        marginTop: 16 
      }}>
        {ordered.map(row => (
          <div key={row.name} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{row.name}</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              You: <span style={{ color: trafficLight(row.org), fontWeight: 700 }}>{row.org}%</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
